/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { usePatchFace } from '@vlabs/api/luna-api/client';
import type { FaceId } from '@vlabs/api/luna-api/model';
import { getCards, getGetCardQueryOptions } from '@vlabs/api/point-api/client';
import type { Card, CardId, GetCardsParams } from '@vlabs/api/point-api/model';
import type { PointGetCardsFilters } from '@vlabs/api/types';
import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { BaseSamplePlaceholder } from '@vlabs/uikit/components/sample/base-sample/BaseSamplePlaceholder';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { toast } from 'react-toastify';

import { FIO_FILTER_KEY } from '@vlabs/monokit/entities/cards/filters/utils';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './AttachFaceToCard.module.sass';
import { checkFacesOnLowSimilarity } from '../../../entities/faces';
import { CardSample } from '../../../shared/ui/card-sample';
import { GoToCardCircleAnimatedButton } from '../../go-to-card';
import { useAttachFaceToCardTranslation } from '../i18n';

interface AttachFaceToCardProps extends
  WithDataTestid {
  faceId: FaceId;
  onSuccess?: (cardId: CardId) => void;
}

interface CardOption {
  value?: string;
  label: string;
  card: Card;
}

const displayName = 'AttachFaceToCard';
const testid = buildTestid(displayName);

function AttachFaceToCard({
  faceId,
  onSuccess = undefined,
  'data-testid': dataTestId = undefined,
}: AttachFaceToCardProps): React.JSX.Element {
  const { t } = useAttachFaceToCardTranslation();
  const queryClient = useQueryClient();
  const [selectedCard, setSelectedCard] = useState<Card>();
  const patchFace = usePatchFace({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getGetCardQueryOptions(selectedCard?.card_id));
        toast.success(t('toast_success'));
        onSuccess?.(selectedCard?.card_id);
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const formatOptionLabel = useCallback(({ label, value }: CardOption) => (
    <div className={st.Option}>
      <span className={st.Fio}>{label}</span>
      <span className={st.Id}>{value}</span>
    </div>
  ), []);

  return (
    <div className={st.Root}>
      <span className={st.Title}>
        {t('title')}
      </span>

      <div className={st.Body}>
        <Select
          cacheOptions
          className={st.SelectWrapper}
          defaultOptions
          formatOptionLabel={formatOptionLabel}
          isAsync
          isClearable
          loadOptions={async (inputValue) => {
            const cardPromises = await Promise.all([
              getCards({
                [FIO_FILTER_KEY]: `%${inputValue}%`,
                page_size: 5,
              } as PointGetCardsFilters as GetCardsParams),
              getCards({
                card_id__ilike: `%${inputValue}%`,
                page_size: 5,
              }),
            ]);
            return cardPromises.flatMap((card) => card.cards).map((card) => ({
              value: card?.card_id,
              label: card?.user_data?.fio ?? card?.card_id,
              card,
            }));
          }}
          onChange={(cardOption) => setSelectedCard((cardOption as CardOption).card)}
          placeholder={t('placeholder')}
        />

        {!selectedCard && (
          <div className={st.Placeholder}>
            <BaseSamplePlaceholder
              placeholderType="image"
            />
          </div>
        )}
        {!!selectedCard?.card_id && (
          <CardSample
            buttonOnTop={(
              <GoToCardCircleAnimatedButton
                cardId={selectedCard.card_id}
                size="small"
              />
            )}
            cardId={selectedCard.card_id}
            className={st.Sample}
          />
        )}
      </div>

      <Control.Button
        className={st.ButtonWrapper}
        disabled={!selectedCard}
        onClick={() => {
          checkFacesOnLowSimilarity({
            reference: { type: 'face', id: faceId },
            candidates: [{
              filters: {
                origin: 'faces',
                external_ids: [selectedCard?.card_id],
              },
            }],
            context: 'addFaceToCard',
          })
            .then(() => {
              patchFace.mutate({
                faceId,
                data: { external_id: selectedCard?.card_id },
              });
            })
            .catch(() => {});
        }}
        {...testid(dataTestId)}
      >
        {t('button_add')}
      </Control.Button>
    </div>
  );
}

AttachFaceToCard.displayName = displayName;

export { AttachFaceToCard };
